<script>
import { getVideoById, getVideosByCategory } from '../api/video';

export default {
  name: 'VideoDetail',
  data() {
    return {
      videoId: null,
      videoData: {
        id: null,
        title: '',
        description: '',
        videoUrl: '',
        coverImage: '',
        duration: 0,
        category: '',
        tags: '',
        author: '',
        viewCount: 0,
        createTime: ''
      },
      relatedVideos: [],
      loading: true
    }
  },
  computed: {
    tagList() {
      if (!this.videoData.tags) return [];
      return this.videoData.tags.split(',').filter(tag => tag.trim());
    }
  },
  methods: {
    async loadVideoDetail() {
      this.loading = true;
      try {
        const res = await getVideoById(this.videoId);
        if (res.data && res.data.data) {
          this.videoData = res.data.data;
          // 加载相关视频
          if (this.videoData.category) {
            this.loadRelatedVideos();
          }
        }
      } catch (error) {
        console.error('加载视频详情失败', error);
        this.$message.error('加载视频失败');
      } finally {
        this.loading = false;
      }
    },

    async loadRelatedVideos() {
      try {
        const res = await getVideosByCategory(this.videoData.category, 6);
        if (res.data && res.data.data) {
          // 过滤掉当前视频
          this.relatedVideos = res.data.data.filter(v => v.id !== this.videoId);
        }
      } catch (error) {
        console.error('加载相关视频失败', error);
      }
    },

    handleVideoPlay() {
      console.log('视频开始播放');
    },

    handleVideoEnded() {
      console.log('视频播放结束');
    },

    openRelatedVideo(id) {
      // 在新标签页打开
      const routeData = this.$router.resolve({ path: '/video/' + id });
      window.open(routeData.href, '_blank');
    },

    formatDuration(seconds) {
      if (!seconds) return '0:00';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      } else {
        return `${minutes}:${String(secs).padStart(2, '0')}`;
      }
    },

    formatViewCount(count) {
      if (!count) return '0';
      if (count >= 10000) {
        return (count / 10000).toFixed(1) + '万';
      }
      return count.toString();
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
  },
  mounted() {
    this.videoId = this.$route.params.id;
    if (this.videoId) {
      this.loadVideoDetail();
    } else {
      this.$message.error('视频ID不存在');
      this.$router.push('/video');
    }
  }
}
</script>

<template>
  <div class="video-detail-page">
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <div v-else class="container">
      <div class="main-content">
        <!-- 视频播放器 -->
        <div class="video-player-container">
          <video
            ref="videoPlayer"
            :src="videoData.videoUrl"
            :poster="videoData.coverImage"
            controls
            controlslist="nodownload"
            @play="handleVideoPlay"
            @ended="handleVideoEnded"
            class="video-player"
          >
            您的浏览器不支持视频播放
          </video>
        </div>

        <!-- 视频信息 -->
        <div class="video-info">
          <h1 class="video-title">{{ videoData.title }}</h1>
          
          <div class="video-meta">
            <span class="meta-item">
              <i class="el-icon-view"></i>
              {{ formatViewCount(videoData.viewCount) }} 次播放
            </span>
            <span class="meta-item" v-if="videoData.createTime">
              <i class="el-icon-time"></i>
              {{ formatDate(videoData.createTime) }}
            </span>
            <span class="meta-item" v-if="videoData.author">
              <i class="el-icon-user"></i>
              {{ videoData.author }}
            </span>
          </div>

          <!-- 分类和标签 -->
          <div class="video-tags" v-if="videoData.category || tagList.length > 0">
            <span class="category-tag" v-if="videoData.category">
              <i class="el-icon-folder"></i>
              {{ videoData.category }}
            </span>
            <span class="tag" v-for="(tag, index) in tagList" :key="index">
              {{ tag }}
            </span>
          </div>

          <!-- 视频描述 -->
          <div class="video-description" v-if="videoData.description">
            <h3 class="section-title">视频简介</h3>
            <p class="description-text">{{ videoData.description }}</p>
          </div>
        </div>
      </div>

      <!-- 相关视频 -->
      <div class="related-videos" v-if="relatedVideos.length > 0">
        <h2 class="section-title">相关推荐</h2>
        <div class="related-grid">
          <div
            class="related-card"
            v-for="item in relatedVideos"
            :key="item.id"
            @click="openRelatedVideo(item.id)"
          >
            <div class="related-cover">
              <img :src="item.coverImage" :alt="item.title" />
              <div class="play-icon">
                <i class="el-icon-video-play"></i>
              </div>
              <div class="duration">{{ formatDuration(item.duration) }}</div>
            </div>
            <div class="related-info">
              <h4 class="related-title">{{ item.title }}</h4>
              <div class="related-meta">
                <span><i class="el-icon-view"></i> {{ formatViewCount(item.viewCount) }} 次播放</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video-detail-page {
  width: 100%;
  min-height: 100vh;
  background: #f5f7fa;
  padding: 20px 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #666;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #667eea;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.main-content {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 40px;
}

.video-player-container {
  width: 100%;
  background: #000;
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
}

.video-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  outline: none;
}

.video-info {
  padding: 30px;
}

.video-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.4;
}

.video-meta {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #666;
  font-size: 14px;

  i {
    color: #667eea;
  }
}

.video-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.category-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.tag {
  padding: 6px 16px;
  background: #f0f0f0;
  color: #666;
  border-radius: 20px;
  font-size: 14px;
}

.video-description {
  margin-top: 30px;
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
  padding-left: 12px;
  border-left: 4px solid #667eea;
}

.description-text {
  font-size: 15px;
  line-height: 1.8;
  color: #666;
  white-space: pre-wrap;
}

.related-videos {
  margin-bottom: 40px;

  .section-title {
    margin-bottom: 25px;
  }
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.related-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

    .play-icon {
      opacity: 1;
    }

    img {
      transform: scale(1.05);
    }
  }
}

.related-cover {
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  background: #000;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
  }
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 40px;
  color: #fff;
  opacity: 0;
  transition: opacity 0.3s;
}

.duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.related-info {
  padding: 12px;
}

.related-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  min-height: 40px;
}

.related-meta {
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: #999;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  i {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .video-title {
    font-size: 22px;
  }

  .video-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .meta-left {
    flex-direction: column;
    gap: 10px;
  }

  .related-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 15px;
  }
}
</style>
